import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputPriceRate from 'components/others/InputPriceRate';
import { Alert, Col, Row } from 'react-bootstrap';
import { useSettings } from 'context/SettingsContext';
import ETiny from 'components/editors/ETiny';
import Editor from 'components/editors/Editor';
import { ClientSelected, OffClientEdit } from 'modules/client';
import type { ClientType } from '@econtractor/types';
import * as apiClient from 'api/apiClients';
import conf from 'conf/conf';
import SolarIcon from 'components/icons/SolarIcon';
import NewObjectCustomerPreview from '../NewObjectCustomerPreview';
import type { NewSaleType } from './types';

interface NewSaleFormProps {
    newSale: NewSaleType;
    setNewSale: React.Dispatch<React.SetStateAction<NewSaleType | null>>;
    hasActiveProposal?: boolean;
    acceptedLoading?: boolean;
}

const patchNewSale = (
    setNewSale: React.Dispatch<React.SetStateAction<NewSaleType | null>>,
    patch: Partial<NewSaleType> | ((prev: NewSaleType) => Partial<NewSaleType>),
) => {
    setNewSale((prev) => {
        if (!prev) return null;
        const nextPatch = typeof patch === 'function' ? patch(prev) : patch;
        return { ...prev, ...nextPatch };
    });
};

const clientFromNewSale = (obj: NewSaleType): ClientType | null => {
    const clientId = Number(obj.clientId ?? 0);
    if (!clientId) return null;
    return {
        ID: clientId,
        clientName: obj.clientName || obj.objectName || '',
        clientPhone: obj.clientPhone || '',
        clientEmail: obj.clientEmail || '',
        clientAddress: '',
        clientCreated: 0,
        clientDescription: null,
        clientLocation: '',
        clientStatus: 0,
        leadsCount: 0,
        ownerId: 0,
        userEmail: '',
        userName: '',
    };
};

const NewSaleForm = ({
    newSale,
    setNewSale,
    hasActiveProposal = false,
    acceptedLoading = false,
}: NewSaleFormProps) => {
    const { t } = useTranslation(['Objects', 'Clients']);
    const { localSettings } = useSettings();
    const refInputPrice = useRef<any>(null);
    const refEdit = useRef<any>(null);
    const TinyEditor = (conf.tinyMode ? ETiny : Editor) as any;
    const refInputObjectName = useRef<HTMLInputElement | null>(null);
    const fromLead = Number(newSale.leadId ?? 0) > 0;
    const selectedClient = useMemo(
        () => clientFromNewSale(newSale),
        [newSale.clientId, newSale.clientName, newSale.clientPhone, newSale.clientEmail, newSale.objectName],
    );
    const [showDescription, setShowDescription] = useState(false);
    const [clientEditId, setClientEditId] = useState<number | null>(null);
    const [isNewClientMode, setIsNewClientMode] = useState(false);

    useEffect(() => {
        if (refInputObjectName.current) {
            refInputObjectName.current.focus();
            refInputObjectName.current.select();
        }
    }, [refInputObjectName]);

    const handleChangeClient = (client: ClientType | null) => {
        if (fromLead) return;
        if (!client) {
            patchNewSale(setNewSale, {
                clientId: 0,
                clientName: '',
                clientPhone: '',
                clientEmail: '',
            });
            return;
        }

        patchNewSale(setNewSale, (prev) => {
            const currentName = (prev.objectName || '').trim();
            const shouldPrefillName = !currentName || currentName === '-';
            return {
                clientId: client.ID,
                clientName: client.clientName || '',
                clientPhone: client.clientPhone || '',
                clientEmail: client.clientEmail || '',
                ...(shouldPrefillName ? { objectName: client.clientName || '' } : {}),
            };
        });
    };

    const hasClient = Number(newSale.clientId ?? 0) > 0;
    const showCreateClientButton = !fromLead && isNewClientMode && hasClient === false;

    const handleCreatedClient = async (clientId: number) => {
        const client = await apiClient.getClientById(clientId);
        if (!client) return;
        handleChangeClient(client);
    };

    return (
        <>
            <Row>
                <Col lg={12}>
                <ClientSelected
                    title={t('labelSetClient')}
                    value={selectedClient}
                    allowNew={!fromLead}
                    disabled={fromLead}
                    clearable={!fromLead}
                    onChange={handleChangeClient}
                    onNewModeChange={setIsNewClientMode}
                />
                <NewObjectCustomerPreview customer={newSale.objectCustomer} />
                {showCreateClientButton ? (
                    <div className="my-2">
                        <button
                            type="button"
                            className="btn btn-link p-0 align-baseline"
                            onClick={() => setClientEditId(0)}
                        >
                            <SolarIcon name="plus" className="me-1" />
                            {t('Clients:toolCreateClient')}
                        </button>
                    </div>
                ) : null}
                </Col>

                {fromLead && !hasActiveProposal && !acceptedLoading ? (
                    <Col lg={12} className="mb-3">
                    <Alert variant="warning" className="mt-2 mb-0 py-2 px-3 fs-12">
                        <SolarIcon name="exclamation-triangle" className="me-2" aria-hidden="true" />
                        {t('noActiveProposalAlert')}
                    </Alert>
                    </Col>
                ) : null}

                <Col lg={12}>
                <div className="mb-3">
                    <label className="form-label text-muted">{t('labelSaleName')}</label>
                    <input
                        autoFocus={true}
                        ref={refInputObjectName}
                        required
                        name="objectName"
                        value={newSale.objectName || ''}
                        onChange={(e) => patchNewSale(setNewSale, { objectName: e.target?.value || '' })}
                        className="form-control"
                    />
                    {!(newSale.objectName || '').trim() ? (
                        <div className="form-text text-danger fs-12 mt-1">{t('saleNameRequiredHint')}</div>
                    ) : null}
                </div>
                </Col>

                <Col lg={12}>
                <div className="mb-3">
                    <label className="form-label text-muted">{t('labelPrice')}</label>
                    <InputPriceRate
                        ref={refInputPrice}
                        value={newSale.objectPrice || 0}
                        onValueChange={(val: any) => {
                            const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
                            const price = Number(val?.floatValue ?? val?.value) || 0;
                            patchNewSale(setNewSale, {
                                objectPrice: price,
                                objectPriceRate: Math.round(price * rate * 100) / 100,
                            });
                        }}
                    />
                </div>
                </Col>

                <div className="mb-3">
                    {showDescription ? (
                        <>
                            <label className="form-label text-muted">{t('labelInfo')}</label>
                            <TinyEditor
                                ref={refEdit}
                                value={newSale.objectInfo || ''}
                                onChange={(newValue: string) => {
                                    patchNewSale(setNewSale, { objectInfo: newValue });
                                }}
                            />
                        </>
                    ) : (
                        <button
                            type="button"
                            className="btn btn-link p-0 align-baseline"
                            onClick={() => setShowDescription(true)}
                        >
                            <SolarIcon name="plus" className="me-1" />
                            {t('btnAddInfo')}
                        </button>
                    )}
                </div>
            </Row>

            <OffClientEdit
                clientId={clientEditId}
                setClientId={setClientEditId}
                fetchClients={async () => {}}
                onCreated={handleCreatedClient}
            />
        </>
    );
};

export default NewSaleForm;
